import { createContext, isValidElement, useCallback, useContext, useId, useRef, useState } from 'react';

import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';

import { useInheritedDarkMode } from '../Theme';
import { cn } from '../utils';
import {
  tooltipDotRecipe,
  tooltipIconRecipe,
  tooltipPopupRecipe,
  tooltipPositionerRecipe,
  tooltipTrackingDotRecipe,
} from './Tooltip.recipe';

import type { TooltipProps, TooltipProviderProps } from './types';

/**
 * How far the pill sits from the trigger, in pixels.
 *
 * Read off the drawing rather than chosen: every one of the eight cells pads its own frame by
 * `x1,5` on the side the dot points from, so the set's own boundary is 6px (gap) + 8px (dot) + 6px
 * (that padding) clear of the pill. All three are tokens, and the sum is the only distance the file
 * states between the pill and the thing it points at.
 *
 * It is also the one number here that is an INFERENCE, because a component set has no trigger in it
 * to measure against — the outer 6px could be breathing room in the file rather than a specified
 * offset. It is consistent across all eight cells and bound to a variable, which is why it is read
 * as authored. Flagged in the PR as the number a usage frame would settle.
 *
 * Note this is not the siblings' 4px: `Menu`, `Select` and `DatePicker` all leave 4px between a
 * control and the surface it opens, and none of them has a pointer to fit in the gap.
 */
const SIDE_OFFSET = 20;
const DEFAULT_HOVER_DELAY = 350;
const TooltipDelayContext = createContext<number | undefined>(undefined);

/*
 * Design System 2.0 tooltip — a short label on hover or focus.
 *
 * Ported from the `Tooltip` component set (file 5aSkE0U4TeWbuIZDvccHjc, node 12:1033). Four things
 * about the port are worth knowing before changing anything:
 *
 *   - the pointer is a DETACHED DOT, not a triangle. The aligned variants pin it to the pill as
 *     drawn; the centred variant tracks the trigger so collision avoidance cannot leave it pointing
 *     beside a button near the viewport edge. `tooltipDotRecipe` has the measured geometry;
 *   - the surface INVERTS, so it has no border and its foregrounds are the `*-inverted` roles. The
 *     `.ds-tooltip` block in `design-system.css` has the numbers;
 *   - the drawn `Placement` axis is split into `side` + `align`, and its `Left`/`Right` names mean
 *     the OPPOSITE of the engine's `-start`/`-end`. See `TooltipSide` for the mapping and why;
 *   - the ICON's colour deviates from what the set binds, because the bound role measures 2.51:1 in
 *     light and 2.15:1 in dark on this surface. Measured 2026-09-17; see `.ds-tooltip`.
 *
 * It animates, which no cell in the set specifies: opacity and scale out of the trigger's edge using
 * the overlay motion roles. Reduced motion keeps the fade and removes the scale. `tooltipPopupRecipe`
 * has the reasoning and the measured computed values.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer, and
 * because the pill is portalled that ancestor is re-resolved from the trigger on every open — see
 * `useInheritedDarkMode`.
 *
 * ── What it is, and what it is not ───────────────────────────────────────────────────────────────
 *
 * The set's description is unusually prescriptive and it is the specification: "A short label on
 * hover or focus. Never put an action or anything the user needs to read to proceed inside one."
 * `TooltipProps.label` is therefore a `string`, so this component cannot become a popover by
 * accident. Disabled focusable buttons also reveal their reason on a touch tap. Essential
 * instructions still belong beside the control.
 *
 * ── The trigger keeps its own element ────────────────────────────────────────────────────────────
 *
 * `Tooltip.Trigger` is rendered AS the caller's child via Base UI's `render` prop, so there is no
 * wrapper element and the hover handlers plus the description land on the real control.
 *
 * That is the fix for a documented defect in the legacy MUI tooltip next door
 * (`common/components/Tooltip`), which wraps its child in a `<span>` and so binds the description to
 * the span rather than to the control. Its own comment says the rest: "Hover and focus still open
 * the tooltip, so the omission is invisible until someone uses a screen reader." It carries a
 * `disableWrapper` escape hatch for exactly this; here there is no wrapper to disable.
 *
 * ── THE ARIA WIRING IS THIS COMPONENT'S, NOT BASE UI'S ───────────────────────────────────────────
 *
 * Verified against the rendered DOM on 2026-09-17, not assumed: Base UI 1.8 gives the popup NO role
 * and the trigger NO `aria-describedby`. That is deliberate on their part — their own docs say
 * "tooltips should act as supplementary visual labels for sighted mouse and keyboard users. Tooltips
 * alone are not accessible to touch or screen reader users."
 *
 * Correct as a general position, and not good enough as a DS default here. The component this
 * replaces DOES announce: MUI puts the title on the child as `aria-label`, or as `aria-describedby`
 * under `describeChild`. Shipping a replacement that announces nothing would be a silent regression
 * on every call site that migrates, and it would fail quietly — the tooltip still appears, so
 * nothing looks broken.
 *
 * So `role="tooltip"` and `aria-describedby` are set HERE, and the division of labour is fixed:
 *
 *   - the TRIGGER names itself. Every DS 2.0 control already does — `Button` and `IconButton` make
 *     `aria-label` a REQUIRED prop the moment they are icon-only, as a type error rather than a
 *     convention — so there is no case where this tooltip is the only name a control has.
 *   - the TOOLTIP describes. `aria-describedby`, not `aria-label`, because the label is extra detail
 *     about a control that is already named, which is what a description is for.
 *
 * The attribute is set only while the tooltip is OPEN, because Base UI unmounts the popup on close
 * and an `aria-describedby` pointing at an absent id is dead weight.
 *
 */
/**
 * A short, non-interactive description on hover or focus. The child control must have its own
 * accessible name; Tooltip adds `aria-describedby` while open and does not name the control.
 * `label` is a string, so an action or essential instruction belongs elsewhere. The trigger
 * keeps its own element and must forward its ref and props.
 *
 * Use `side` and `align` for placement. Figma `TopLeft` maps to `side="top" align="end"`;
 * `TopRight` maps to `side="top" align="start"`. The same left/right reversal applies to bottom.
 * For an icon-only action:
 * `<Tooltip label="Copy link" side="top" align="end"><IconButton aria-label="Copy link"><CopyIcon /></IconButton></Tooltip>`.
 * The surface is inverted and has a detached dot, not a standard tooltip arrow.
 * `closeOnClick` defaults to true; set it false only when the trigger itself toggles the tooltip
 * on touch, as InfoButton does. Disabled focusable buttons toggle on touch automatically.
 */
export const Tooltip = ({
  label,
  icon,
  children,
  side = 'top',
  align = 'center',
  open,
  onOpenChange,
  delay,
  closeOnClick,
  className,
  ...rest
}: TooltipProps) => {
  /**
   * Open state is tracked here even when the caller controls it, exactly as in `DatePicker`: the
   * portalled pill has to re-resolve its theme from the trigger every time it opens, and
   * `useInheritedDarkMode` needs a value this component can read to do it.
   */
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = open ?? uncontrolledOpen;
  const providerDelay = useContext(TooltipDelayContext);
  const disabledTrigger =
    isValidElement<{
      disabled?: boolean;
      loading?: boolean;
      isLoading?: boolean;
      'aria-disabled'?: boolean | 'true' | 'false';
    }>(children) &&
    (children.props.disabled ||
      children.props.loading ||
      children.props.isLoading ||
      children.props['aria-disabled'] === true ||
      children.props['aria-disabled'] === 'true');

  /**
   * The trigger doubles as the theme sentinel — it is a real node at the call site's position in the
   * tree, which is what the hook needs to walk up from. So like `Menu`, and unlike `Dialog` and
   * `Select`, this component renders no hidden sentinel element of its own.
   */
  const [trigger, setTrigger] = useState<HTMLElement | null>(null);
  const touchPress = useRef<{ pointerId: number; open: boolean } | null>(null);
  const touchOpened = useRef(false);
  const { inheritedDarkClass } = useInheritedDarkMode(isOpen, trigger);

  /** Ours rather than Base UI's, because Base UI does not wire the description — see above. */
  const labelId = useId();

  // Base UI hands out an `eventDetails` second argument that this API has no use for.
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange]
  );

  return (
    <BaseTooltip.Root
      disabled={!label}
      open={!!label && isOpen}
      onOpenChange={(nextOpen, details) => {
        if (
          !nextOpen &&
          touchOpened.current &&
          (details.reason === 'trigger-hover' || details.reason === 'trigger-focus')
        ) {
          // A tap's compatibility hover/blur events must not dismiss the reason it just revealed.
          details.cancel();
          return;
        }
        const triggerPress =
          details.reason === 'trigger-press' ||
          (details.reason === 'outside-press' &&
            details.event.target instanceof Node &&
            trigger?.contains(details.event.target));
        if (!nextOpen && triggerPress && trigger?.getAttribute('aria-disabled') === 'true') {
          // Chromium sends a compatibility click after touch release. It belongs to the tap that
          // just opened the reason, even if Base UI has not registered the popup's anchor yet.
          details.cancel();
          return;
        }
        if (!nextOpen) touchOpened.current = false;
        handleOpenChange(nextOpen);
      }}
    >
      {/* `render` puts the trigger's behaviour on the CALLER'S element rather than on a wrapper —
          see the note above about the legacy tooltip's `aria-describedby`. The ref is forwarded to
          that same element, which is what makes it usable as the theme sentinel.

          `delay` belongs to the TRIGGER in this version of Base UI, not to the root and not only to
          the provider — so a per-tooltip override needs no `Tooltip.Provider` around the call site.
          The local default is shorter than Base UI's 600ms. The provider's delay still takes
          precedence unless this trigger sets its own. */}
      <BaseTooltip.Trigger
        ref={setTrigger}
        // Only while open: Base UI unmounts the popup on close, and a dangling reference is dead
        // weight. The trigger's own NAME is the caller's — see the ARIA note above.
        aria-describedby={label && isOpen ? labelId : undefined}
        closeOnClick={disabledTrigger ? false : closeOnClick}
        onPointerMoveCapture={(event) => {
          // Real cursor movement restores hover dismissal on devices with both touch and a mouse.
          if (event.pointerType !== 'touch') touchOpened.current = false;
        }}
        onPointerDownCapture={(event) => {
          if (event.pointerType === 'touch' && event.currentTarget.getAttribute('aria-disabled') === 'true') {
            touchPress.current = { pointerId: event.pointerId, open: isOpen };
          }
        }}
        onPointerCancel={() => {
          touchPress.current = null;
        }}
        onPointerUp={(event) => {
          const press = touchPress.current;
          touchPress.current = null;
          if (
            event.pointerType === 'touch' &&
            press?.pointerId === event.pointerId &&
            event.currentTarget.getAttribute('aria-disabled') === 'true'
          ) {
            // Base UI blocks a disabled button's click and pointerdown handlers. A completed tap
            // can still explain it on pointerup, without activating the action or a form submit.
            touchOpened.current = !press.open;
            handleOpenChange(!press.open);
          }
        }}
        delay={delay ?? providerDelay ?? DEFAULT_HOVER_DELAY}
        render={children}
      />

      {/* The theme class goes on the PORTAL, which is an ancestor of the pill — so the pill carries
          only its own `ds-tooltip` slot class and the ordinary `:where(.dark) .ds-tooltip` cascade
          reaches it, exactly as for a tooltip that never portalled. */}
      <BaseTooltip.Portal className={inheritedDarkClass}>
        {/* The centred dot uses the positioning engine's arrow coordinates when the pill shifts to
            avoid a viewport edge. Aligned variants keep the pill-pinned positions drawn in Figma. */}
        <BaseTooltip.Positioner
          align={align}
          // Corner dots are flush with the pill. Offset the pill by half the trigger less
          // the 4px dot radius, so the dot points at the control's centre, not its edge.
          alignOffset={({ align: resolvedAlign, anchor, side: resolvedSide }) =>
            resolvedAlign === 'center'
              ? 0
              : (resolvedSide === 'top' || resolvedSide === 'bottom' ? anchor.width : anchor.height) / 2 - 4
          }
          className={tooltipPositionerRecipe()}
          side={side}
          sideOffset={SIDE_OFFSET}
        >
          {/* `role` and `id` are both this component's — Base UI renders the popup as a bare `div`.
              See the ARIA note above for why that is their decision and why it is overridden here. */}
          <BaseTooltip.Popup
            className={cn(tooltipPopupRecipe(), className)}
            data-testid={rest['data-testid']}
            id={labelId}
            role="tooltip"
          >
            {icon && (
              <span aria-hidden className={tooltipIconRecipe()} data-part="icon">
                {icon}
              </span>
            )}
            {label}

            {/* Centred labels can shift away from the trigger at a viewport edge. Let Base UI keep
                their dot aimed at the trigger; for start/end, preserve the set's flush-edge dot.
                Both stay inside the popup so they share its theme, fade and scale. */}
            {align === 'center' ? (
              <BaseTooltip.Arrow className={tooltipTrackingDotRecipe()} data-part="dot" />
            ) : (
              <span aria-hidden className={tooltipDotRecipe()} data-part="dot" />
            )}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
};

/**
 * Groups the tooltips inside it, so that once one is showing its neighbours open instantly.
 *
 * Without it every trigger waits out its full delay on its own, so running the pointer along a
 * toolbar made each label wait 350ms again as if the user had never seen the first one — and the
 * pill's `data-[instant]` rule, which skips the entrance for exactly that move, never fired, because
 * Base UI only enters its instant phase inside a provider. Wrap a toolbar, a control bar or the app
 * root once; a provider nested inside another starts a group of its own.
 */
export const TooltipProvider = ({ children, delay, timeout }: TooltipProviderProps) => (
  <TooltipDelayContext.Provider value={delay}>
    <BaseTooltip.Provider delay={delay ?? DEFAULT_HOVER_DELAY} timeout={timeout}>
      {children}
    </BaseTooltip.Provider>
  </TooltipDelayContext.Provider>
);
